import { Plus, Trash2 } from 'lucide-react';
import type { ModeConfig } from '../lib/modes';
import { HelpText } from './HelpText';

// ─────────────────────────────────────────────────────────────────────────────
// GameModesEditor — the game-mode DEFINITIONS table (name/short/type/win-at/
// multiplier/unit + add/remove rows). "Multiplier" is the display label for
// the underlying `seedingPointsPerUnit` field (Lisa's QA: "it's like a
// multiplier" — label-only rename, the field name itself is unchanged).
//
// T5 (modes-per-stage): extracted from CompetitionCreateForm's original
// comp-level-only ModeEditor so the SAME markup/logic drives BOTH the
// comp-level "Game Modes" section (the template/defaults — the Ruleset
// preset seeds it, and every new stage starts from it) AND each stage's own
// "Game modes (this stage)" override block (StagePipelinePanel.tsx's
// StageConfigFields) once an admin opts a stage OUT of inheriting the comp's
// modes — defined ONCE, used in both places, same precedent
// SeriesFormatEditor.tsx already set for the per-stage series block.
// Pure/presentational — no hooks, no network calls.
// ─────────────────────────────────────────────────────────────────────────────

export const EMPTY_MODE: ModeConfig = { key: '', label: '', shortLabel: '', type: 'score', winScore: 0 };

export function GameModesEditor({ modes, onChange }: { modes: ModeConfig[]; onChange: (m: ModeConfig[]) => void }) {
  const update = (i: number, partial: Partial<ModeConfig>) => {
    const next = [...modes];
    next[i] = { ...next[i], ...partial };
    if (partial.label && !modes[i].key) {
      next[i].key = partial.label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    }
    if (partial.label && !modes[i].shortLabel) {
      next[i].shortLabel = partial.label.slice(0, 4).toUpperCase();
    }
    onChange(next);
  };
  const remove = (i: number) => onChange(modes.filter((_, j) => j !== i));
  const add = () => onChange([...modes, { ...EMPTY_MODE }]);

  return (
    <div className="space-y-2">
      <HelpText>
        <p className="text-[11px] text-ink-meta">Multiplier = points a team banks per point or round won in this mode. Mode score × multiplier feeds the seeding standings.</p>
      </HelpText>
      {/* This row's column widths are fixed px (name column aside) so the
          form fields keep a consistent, comfortably-typeable size — that
          means the row itself doesn't shrink to phone width. Scope the
          overflow to THIS block (own overflow-x-auto) rather than letting it
          force the whole page wider — same idiom StandingsTable's table
          wrapper uses. */}
      <div className="overflow-x-auto">
        <div className="space-y-2">
          {modes.map((m, i) => (
            <div key={i} className="grid min-w-[620px] grid-cols-[1fr_100px_110px_96px_96px_110px_32px] gap-2 items-end">
              <div>
                {i === 0 && <label className="label">Mode name</label>}
                <input value={m.label} onChange={e => update(i, { label: e.target.value })} placeholder="Hardpoint" className="input w-full text-xs" />
              </div>
              <div>
                {i === 0 && <label className="label">Short</label>}
                <input value={m.shortLabel} onChange={e => update(i, { shortLabel: e.target.value })} placeholder="HP" className="input w-full text-xs" />
              </div>
              <div>
                {i === 0 && <label className="label">Type</label>}
                <select value={m.type} onChange={e => update(i, { type: e.target.value as 'round' | 'score' })} className="input w-full text-xs">
                  <option value="score">Score</option>
                  <option value="round">Round</option>
                </select>
              </div>
              <div>
                {i === 0 && <label className="label">Win at</label>}
                <input type="number" value={m.winScore} onChange={e => update(i, { winScore: Number(e.target.value) })} className="input w-full text-xs" />
              </div>
              <div>
                {i === 0 && <label className="label">Multiplier</label>}
                <input type="number" value={m.seedingPointsPerUnit ?? 0} onChange={e => update(i, { seedingPointsPerUnit: Number(e.target.value) })} className="input w-full text-xs" />
              </div>
              <div>
                {i === 0 && <label className="label">Unit</label>}
                <input value={m.seedingUnitLabel ?? ''} onChange={e => update(i, { seedingUnitLabel: e.target.value })} placeholder="point" className="input w-full text-xs" />
              </div>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300 pb-1" title="Remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <button onClick={add} className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300">
        <Plus className="h-3 w-3" /> Add mode
      </button>
    </div>
  );
}
